import React from 'react';

import { DetailsListItem } from '@eika/details-list';

import type { InterestRate } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import { InterestType } from '../../../translations/translationType';
import { currencyFormatter } from '../../shared/numberFormat';

interface ListProps {
    interests?: InterestRate[];
    type: InterestType;
}

const InterestList: React.FC<ListProps> = ({ interests, type }) => {
    const translated = useTranslation();
    const { types } = translated.accountInterest;
    const currency = 'NOK';

    const labels = types[type];

    if (!interests) {
        return <></>;
    }

    const formatLevel = (amount: number, type: InterestType) => {
        if (type === InterestType.DEBIT) {
            return currencyFormatter(amount, currency);
        }
        return `${amount}%`;
    };

    const interestLabel = (amount: number, prevAmount?: number, isLast = true) => {
        if (prevAmount) {
            return `${labels.above} ${formatLevel(prevAmount, type)}`;
        }

        if (isLast) {
            return prevAmount ? `${labels.above} ${formatLevel(prevAmount, type)}` : `${labels.interest}`;
        }
        return `${labels.upTo} ${formatLevel(amount, type)}`;
    };
    const compareNumber = (num1: number | undefined, num2: number | undefined) => {
        return (num1 ?? 0) === (num2 ?? 0);
    };
    const uniqueInterests = interests.reduce((result: InterestRate[], interest: InterestRate) => {
        const hit = result.some(
            (r) =>
                compareNumber(r.minimumAmount, interest.minimumAmount) &&
                compareNumber(r.maximumAmount, interest.maximumAmount) &&
                r.rate === interest.rate,
        );
        if (!hit) {
            return [...result, interest];
        }
        return result;
    }, []);

    const { length } = uniqueInterests;
    return (
        <>
            {uniqueInterests.map((interest, index) => {
                const prev = index > 0 ? uniqueInterests[index - 1].maximumAmount : undefined;
                const isLast = index === length - 1;
                return (
                    <DetailsListItem
                        label={interestLabel(interest.maximumAmount!, prev, isLast)}
                        value={`${interest.rate} %`}
                        key={`account-details__interest-${interest.maximumAmount}`}
                    />
                );
            })}
        </>
    );
};

export default InterestList;
